/**
 * useSprintConfig — fetch the sprint window for the caller's tenant.
 * Cached per tenant for the session lifetime.
 *
 * Returns { sprintStart, sprintEnd, daysRemaining, totalDays, weeksRemaining }.
 */
import { useEffect, useState } from "react";
import { supabase, getCurrentTenant } from "@/lib/session";

export interface SprintConfig {
  tenantId: number;
  sprintStart: string;   // ISO date
  sprintEnd: string;     // ISO date
  sprintLabel: string;
  boyLabel: string;
}

export interface SprintWindow extends SprintConfig {
  daysRemaining: number;
  daysElapsed: number;
  totalDays: number;
  weeksRemaining: number;
  inSprint: boolean;
}

const _cache = new Map<number, SprintConfig>();
const _inflight = new Map<number, Promise<SprintConfig | null>>();

async function fetchConfig(tenantId: number): Promise<SprintConfig | null> {
  const existing = _inflight.get(tenantId);
  if (existing) return existing;
  const p = (async () => {
    const sb = supabase();
    const { data, error } = await sb
      .from("sprint_config")
      .select("tenant_id, sprint_start, sprint_end, sprint_label, boy_label")
      .eq("tenant_id", tenantId)
      .limit(1);
    if (error || !data || data.length === 0) return null;
    const row = data[0] as any;
    const cfg: SprintConfig = {
      tenantId: row.tenant_id,
      sprintStart: row.sprint_start,
      sprintEnd: row.sprint_end,
      sprintLabel: row.sprint_label || "Sprint",
      boyLabel: row.boy_label || "Balance of year",
    };
    _cache.set(tenantId, cfg);
    return cfg;
  })();
  _inflight.set(tenantId, p);
  try { return await p; } finally { _inflight.delete(tenantId); }
}

function deriveWindow(cfg: SprintConfig | null): SprintWindow | null {
  if (!cfg) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(cfg.sprintStart);
  const end = new Date(cfg.sprintEnd);
  const dayMs = 24 * 60 * 60 * 1000;
  const totalDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / dayMs));
  const daysRemaining = Math.max(0, Math.round((end.getTime() - today.getTime()) / dayMs));
  const daysElapsed = Math.max(0, totalDays - daysRemaining);
  const weeksRemaining = Math.max(0, Math.ceil(daysRemaining / 7));
  const inSprint = today >= start && today <= end;
  return { ...cfg, daysRemaining, daysElapsed, totalDays, weeksRemaining, inSprint };
}

export function useSprintConfig() {
  const tenant = getCurrentTenant();
  const tenantId = tenant?.id ?? null;
  const cached = tenantId != null ? _cache.get(tenantId) ?? null : null;
  const [cfg, setCfg] = useState<SprintConfig | null>(cached);
  const [loading, setLoading] = useState<boolean>(!cached && tenantId != null);

  useEffect(() => {
    if (tenantId == null) { setCfg(null); setLoading(false); return; }
    const c = _cache.get(tenantId);
    if (c) { setCfg(c); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    fetchConfig(tenantId).then((r) => {
      if (!cancelled) { setCfg(r); setLoading(false); }
    }).catch(() => {
      if (!cancelled) { setCfg(null); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [tenantId]);

  return { config: cfg, window: deriveWindow(cfg), loading };
}

/** Compute goal-track for an opportunity given an expected close date. */
export function deriveGoalTrack(expectedCloseISO: string | null | undefined, sprintEndISO: string | null | undefined): "sprint" | "boy" | "unknown" {
  if (!expectedCloseISO || !sprintEndISO) return "unknown";
  const close = new Date(expectedCloseISO).getTime();
  const end = new Date(sprintEndISO).getTime();
  if (Number.isNaN(close) || Number.isNaN(end)) return "unknown";
  return close <= end ? "sprint" : "boy";
}
